import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { thenElse } from 'dg-web-shared/lib/MaybeV2.ts';
import { stringifyParams } from 'dg-web-shared/lib/QueryParamsUtil.ts';
import { ElementNamer } from 'dg-web-shared/lib/ReactHelpers.tsx';
import * as Http from '../../../api/Http.ts';
import * as OperatorDataState from '../../../common/state/OperatorDataState.ts';
import {
    FilterLayout,
    FilterLayoutBody,
    FilterLayoutHeader,
} from '../../../ui/filter/FilterLayout.tsx';
import {
    ButtonDropdown,
    ButtonDropdownLinkItem,
} from '../../../ui/slidein/ButtonDropdown.tsx';
import * as FilteredPermitsState from '../../state/FilteredPermitsState.ts';
import { AddMenu } from '../shared/AddMenu.tsx';
import { CostFilterHalfSlideIn } from './CostFilter.tsx';
import { FilteredPermitsFilterHalfSlideIn } from './FilteredPermitsFilterHalfSlideIn.tsx';
import { FilteredPermitsFilterSummary } from './FilteredPermitsFilterSummary.tsx';
import { FilteredPermitsList } from './FilteredPermitsList.tsx';
import { IssueDateFilterHalfSlideIn } from './IssueDateFilter.tsx';
import { OperatorLoginFilterHalfSlideIn } from './OperatorLoginFilter.tsx';
import { PermitTypeFilterHalfSlideIn } from './PermitTypeFilter.tsx';
import { StatusFilterHalfSlideIn } from './StatusFilter.tsx';
import { TimeLimitFilterHalfSlideIn } from './TimeLimitFilter.tsx';
import { ValidDateFilterHalfSlideIn } from './ValidDateFilter.tsx';
import { ChannelFilterHalfSlideIn } from './ChannelFilter.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { OperatorContext } from '../../../app/components/BaseLayoutAndData.tsx';
import { urlWithOperatorExternalId } from '../../../utils/Utils.ts';
import { useContext } from 'react';

export const renderPermitFilterSlideIns = () => (
    <>
        <FilteredPermitsFilterHalfSlideIn />
        <ValidDateFilterHalfSlideIn />
        <PermitTypeFilterHalfSlideIn />
        <OperatorLoginFilterHalfSlideIn />
        <IssueDateFilterHalfSlideIn />
        <TimeLimitFilterHalfSlideIn />
        <ChannelFilterHalfSlideIn />
        <CostFilterHalfSlideIn />
        <StatusFilterHalfSlideIn />
    </>
);

const filterToParams = (
    filter: FilteredPermitsState.Filter.State,
    language: string,
): string => {
    return stringifyParams(
        Http.OperatorAccount.Permits.filteredListParamsToQueryParams({
            validDateFrom: filter.validDateFrom,
            validDateTo: filter.validDateTo,
            permitType: filter.permitTypes,
            issuedBy: thenElse(
                filter.operatorLogins,
                l =>
                    l.map(id =>
                        id ===
                        FilteredPermitsState.Filter
                            .OperatorLoginFilterOnlineOption
                            ? null
                            : id,
                    ),
                null,
            ),
            issueDateFrom: filter.issueDateFrom,
            issueDateTo: filter.issueDateTo,
            timeLimit: filter.timeLimit,
            channel: filter.channel,
            cost: filter.cost,
            status: filter.status,
            lang: language,
            sortField: 'validTo',
        }),
    );
};

const cn = ElementNamer('FilteredPermits');

export function FilteredPermits() {
    const { operatorExternalId } = useContext(OperatorContext);
    const { storeState } = useStore(store => ({
        filter: FilteredPermitsState.Filter.get(store),
        operator: OperatorDataState.get(store),
    }));
    const { language } = useLanguage();

    const filterParams = filterToParams(storeState.filter, language);
    const filterActive = FilteredPermitsState.Filter.filterActive(
        storeState.filter,
    );

    return (
        <FilterLayout>
            <FilterLayoutHeader
                filterSummary={<FilteredPermitsFilterSummary />}
                rightDropdown={<AddMenu label={captureText} />}
                secondRightDropdown={
                    <ButtonDropdown label={exportText}>
                        <ButtonDropdownLinkItem
                            href={urlWithOperatorExternalId(
                                `/ui-api/operator-account/permits/csv-export?${filterParams}`,
                                operatorExternalId,
                            )}
                            label={
                                <Localized
                                    de="Bewilligungen"
                                    fr="Autorisations"
                                    it="Autorizzazioni"
                                    en="Permissions"
                                />
                            }
                            disabled={!filterActive}
                        />
                        <ButtonDropdownLinkItem
                            href={urlWithOperatorExternalId(
                                `/ui-api/operator-account/permits/by-entity-csv-export?${filterParams}`,
                                operatorExternalId,
                            )}
                            label={
                                <Localized
                                    de="Bewilligungen pro Kennzeichen / Badge"
                                    fr="Autorisations par immatriculation / badge"
                                    it="Autorizzazioni per targa / badge"
                                    en="License plate / badge authorizations"
                                />
                            }
                            disabled={!filterActive}
                        />
                        <ButtonDropdownLinkItem
                            href={urlWithOperatorExternalId(
                                `/ui-api/operator-account/permits/by-zone-csv-export?${filterParams}`,
                                operatorExternalId,
                            )}
                            label={
                                <Localized
                                    de="Bewilligungen pro Zone"
                                    fr="Autorisations par zone"
                                    it="Autorizzazioni per zona"
                                    en="Permissions per zone"
                                />
                            }
                            disabled={!filterActive}
                        />
                        <ButtonDropdownLinkItem
                            href={urlWithOperatorExternalId(
                                `/ui-api/operator-account/permits/per-zone-and-day-export?${filterParams}`,
                                operatorExternalId,
                            )}
                            label={
                                <Localized
                                    de="Anzahl Bewilligungen pro Tag, Zone und Art"
                                    fr="Quantité d'autorisations par jour, zone et type"
                                    it="Quantità di autorizzazioni per giorno, zona e tipo"
                                    en="Quantity of permits per day, zone and type"
                                />
                            }
                            disabled={!filterActive}
                        />

                        {storeState.operator.data &&
                            storeState.operator.data.licensePlatePermitSettings
                                .hasSalaryDeductionExport && (
                                <ButtonDropdownLinkItem
                                    href={urlWithOperatorExternalId(
                                        `/ui-api/operator-account/permits/salary-deduction-export?${filterParams}`,
                                        operatorExternalId,
                                    )}
                                    label={
                                        <Localized
                                            de="Lohnabzug"
                                            fr="Retenue sur le salaire"
                                            it="Deduzione dal salario"
                                            en="Deduction from salary"
                                        />
                                    }
                                    disabled={!filterActive}
                                />
                            )}
                    </ButtonDropdown>
                }
            />
            <FilterLayoutBody>
                <FilteredPermitsList />
            </FilterLayoutBody>
        </FilterLayout>
    );
}

export const captureText = (
    <Localized de="Erfassen" fr="Saisir" it="Registra" en="Capture" />
);

export const exportText = (
    <Localized de="Exportieren" fr="Exporter" it="Esporta" en="Export" />
);

FilteredPermits.displayName = cn('');
